import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { clearUser, setUser } from "../store/authSlice";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  AccountInfo,
  EventType,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

type AuthContextValue = {
  isAuthenticated: boolean;
  inProgress: InteractionStatus;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
};

// const DEFAULT_SCOPES = ["api://7dd52d3b-52eb-47dd-b2e9-a09ff954fbe2/api-access"]; // must match your API's exposed scope
const DEFAULT_SCOPES = [import.meta.env.VITE_API_SCOPE];

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts?.[0] ?? null;

  // Keep Redux UI pieces working without changing UI components
  useEffect(() => {
    if (account) {
      dispatch(setUser(account));
    } else {
      dispatch(clearUser());
      // Clear token when no account
      try {
        localStorage.removeItem("access_token");
      } catch {}
    }
  }, [account, dispatch]);

  // Acquire token on initial auth to populate localStorage
  useEffect(() => {
    if (account && isAuthenticated) {
      const acquireToken = async () => {
        const req = {
          account: account,
          scopes: DEFAULT_SCOPES,
        };
        try {
          const result = await instance.acquireTokenSilent(req);
          if (result?.accessToken) {
            localStorage.setItem("access_token", result.accessToken);
            console.log("✅ Access token stored in localStorage");
          }
        } catch (err) {
          console.error("❌ Token acquisition failed, clearing storage and forcing re-login:", err);
          // Clear token from localStorage to force fresh login
          localStorage.removeItem("access_token");
          // Trigger logout to clear MSAL cache and force re-authentication
          instance.logoutRedirect({ postLogoutRedirectUri: "/" });
        }
      };
      acquireToken();
    }
  }, [account, isAuthenticated, instance]);

  const login = useCallback(async () => {
    await instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      redirectStartPage: window.location.pathname + window.location.search,
    });
  }, [instance]);

  const logout = useCallback(async () => {
    // Mark a logout so guards won't auto-login on the next navigation
    try {
      localStorage.setItem("justLoggedOut", "1");
      localStorage.removeItem("access_token"); // Clear token on logout
    } catch {}
    // Use popup to avoid full-page roundtrip
    await instance.logoutPopup({ postLogoutRedirectUri: "/" });
  }, [instance]);

  const getAccessToken = useCallback(
    async (scopes?: string[]) => {
      const req = {
        account: account ?? undefined,
        scopes: scopes ?? DEFAULT_SCOPES,
      };
      try {
        const result = await instance.acquireTokenSilent(req);
        const token = result?.accessToken ?? null;
        console.log(result);
        console.log(token);
        // Store token in localStorage for AxiosClient interceptor
        if (token) {
          try {
            localStorage.setItem("access_token", token);
          } catch (e) {
            console.error("Failed to store access token:", e);
          }
        }

        return token;
      } catch (err) {
        console.error("❌ Token acquisition failed, clearing storage and forcing re-login:", err);
        // Clear token from localStorage
        localStorage.removeItem("access_token");
        // Force logout and re-authentication
        await instance.logoutRedirect({ postLogoutRedirectUri: "/" });
        return null;
      }
    },
    [instance, account]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      inProgress,
      account,
      login,
      logout,
      getAccessToken,
    }),
    [isAuthenticated, inProgress, account, login, logout, getAccessToken]
  );

  // Also set the flag when MSAL reports logout success (covers all logout entry points)
  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        try {
          localStorage.setItem("justLoggedOut", "1");
          localStorage.removeItem("access_token"); // Clear token on logout
        } catch {}
      }
    });
    return () => {
      if (cbId) instance.removeEventCallback(cbId);
    };
  }, [instance]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
